import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FolderOpen, FolderPlus, Upload, FileText, Trash2,
  ArrowLeft, Download, MoreVertical, File, Image,
  FileSpreadsheet, Pencil, FolderInput, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' },
  'image/jpeg': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  'image/png': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  'image/webp': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, color: 'text-teal-500', bg: 'bg-teal-50' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileSpreadsheet, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  'text/plain': { icon: FileText, color: 'text-slate-500', bg: 'bg-slate-50' },
};

const getFileIcon = (contentType) => FILE_ICONS[contentType] || { icon: File, color: 'text-slate-400', bg: 'bg-slate-50' };

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const EmergencyWorkPage = () => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [foldersRes, filesRes] = await Promise.all([
        fetch(`${API}/emergency-work/folders`, { credentials: 'include' }),
        fetch(`${API}/emergency-work/files?${currentFolderId ? `folder_id=${currentFolderId}` : ''}`, { credentials: 'include' }),
      ]);
      if (foldersRes.ok) {
        const allFolders = await foldersRes.json();
        setFolders(allFolders.filter(f => f.parent_id === currentFolderId));
      }
      if (filesRes.ok) setFiles(await filesRes.json());
    } catch (e) { console.error('Failed to fetch:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [currentFolderId]);

  const navigateToFolder = async (folderId, folderName) => {
    setLoading(true);
    if (folderId === null) {
      setBreadcrumb([]);
    } else {
      setBreadcrumb(prev => [...prev, { id: folderId, name: folderName }]);
    }
    setCurrentFolderId(folderId);
    setContextMenu(null);
  };

  const navigateBreadcrumb = (index) => {
    if (index === -1) {
      setBreadcrumb([]);
      setCurrentFolderId(null);
    } else {
      const target = breadcrumb[index];
      setBreadcrumb(breadcrumb.slice(0, index + 1));
      setCurrentFolderId(target.id);
    }
    setLoading(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const r = await fetch(`${API}/emergency-work/folders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name: newFolderName.trim(), parent_id: currentFolderId }),
      });
      if (r.ok) {
        toast.success('Folder created');
        setNewFolderOpen(false);
        setNewFolderName('');
        fetchData();
      }
    } catch (e) { toast.error('Failed to create folder'); }
  };

  const handleUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    if (uploadFiles.length === 0) return;
    setUploading(true);
    let success = 0;
    for (const file of uploadFiles) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const url = `${API}/emergency-work/upload${currentFolderId ? `?folder_id=${currentFolderId}` : ''}`;
        const r = await fetch(url, { method: 'POST', credentials: 'include', body: formData });
        if (r.ok) success++;
      } catch (err) { console.error(`Upload failed for ${file.name}:`, err); }
    }
    setUploading(false);
    if (success > 0) toast.success(`${success} file${success > 1 ? 's' : ''} uploaded`);
    if (success < uploadFiles.length) toast.error(`${uploadFiles.length - success} upload(s) failed`);
    fileInputRef.current.value = '';
    fetchData();
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder and all its files?')) return;
    try {
      await fetch(`${API}/emergency-work/folders/${folderId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Folder deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete folder'); }
    setContextMenu(null);
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await fetch(`${API}/emergency-work/files/${fileId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('File deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete file'); }
    setContextMenu(null);
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const r = await fetch(`${API}/emergency-work/download/${fileId}`, { credentials: 'include' });
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error('Download failed'); }
  };

  const handleRename = async () => {
    if (!renameName.trim() || !renameTarget) return;
    try {
      const r = await fetch(`${API}/emergency-work/folders/${renameTarget}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name: renameName.trim() }),
      });
      if (r.ok) {
        toast.success('Folder renamed');
        setRenameOpen(false);
        setBreadcrumb(prev => prev.map(b => b.id === renameTarget ? { ...b, name: renameName.trim() } : b));
        fetchData();
      }
    } catch (e) { toast.error('Failed to rename'); }
  };

  const pageBreadcrumb = [{ label: 'Emergency Work' }];

  return (
    <Layout breadcrumb={pageBreadcrumb}>
      <div className="max-w-4xl mx-auto space-y-5" data-testid="emergency-work-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900">Emergency Work</h1>
            <p className="text-slate-500 mt-0.5 text-sm">Upload and organise worksheets for when you need them</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setNewFolderOpen(true)} className="border-slate-200 rounded-xl" data-testid="new-folder-btn">
              <FolderPlus className="w-4 h-4 mr-2" />New Folder
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="upload-btn">
              <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.webp"
              onChange={handleUpload}
              className="hidden"
              data-testid="file-input"
            />
          </div>
        </div>

        {/* Breadcrumb navigation */}
        <div className="flex items-center gap-1.5 text-sm">
          <button onClick={() => navigateBreadcrumb(-1)} className={`font-medium transition-colors ${breadcrumb.length > 0 ? 'text-teal-600 hover:text-teal-700' : 'text-slate-700'}`} data-testid="breadcrumb-root">
            <FolderOpen className="w-4 h-4 inline mr-1" />All Files
          </button>
          {breadcrumb.map((b, i) => (
            <span key={b.id} className="flex items-center gap-1.5">
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              <button
                onClick={() => navigateBreadcrumb(i)}
                className={`font-medium transition-colors ${i === breadcrumb.length - 1 ? 'text-slate-700' : 'text-teal-600 hover:text-teal-700'}`}
                data-testid={`breadcrumb-${b.id}`}
              >
                {b.name}
              </button>
            </span>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading...</div>
        ) : folders.length === 0 && files.length === 0 ? (
          <Card className="border border-dashed border-slate-300 bg-white shadow-none">
            <CardContent className="py-16 text-center">
              <FolderOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-600 mb-1">
                {currentFolderId ? 'This folder is empty' : 'No files yet'}
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Upload worksheets, activities, and emergency resources
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={() => setNewFolderOpen(true)} className="rounded-xl">
                  <FolderPlus className="w-4 h-4 mr-1.5" />New Folder
                </Button>
                <Button size="sm" onClick={() => fileInputRef.current?.click()} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  <Upload className="w-4 h-4 mr-1.5" />Upload
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {/* Folders */}
            {folders.map(folder => (
              <div
                key={folder.folder_id}
                className="group flex items-center gap-3 p-3.5 bg-white border border-slate-200 rounded-xl hover:border-teal-200 hover:bg-teal-50/30 transition-all cursor-pointer"
                onClick={() => navigateToFolder(folder.folder_id, folder.name)}
                data-testid={`folder-${folder.folder_id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{folder.name}</p>
                  <p className="text-xs text-slate-400">Folder</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setRenameTarget(folder.folder_id); setRenameName(folder.name); setRenameOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid={`rename-folder-${folder.folder_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDeleteFolder(folder.folder_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-folder-${folder.folder_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

            {/* Files */}
            {files.map(file => {
              const fi = getFileIcon(file.content_type);
              const Icon = fi.icon;
              return (
                <div
                  key={file.file_id}
                  className="group flex items-center gap-3 p-3.5 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all"
                  data-testid={`file-${file.file_id}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${fi.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${fi.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{file.original_filename}</p>
                    <p className="text-xs text-slate-400">{formatSize(file.size)}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleDownload(file.file_id, file.original_filename)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid={`download-file-${file.file_id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDeleteFile(file.file_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-file-${file.file_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* New Folder Dialog */}
        <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-slate-900">New Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <Input
                placeholder="e.g., Year 7 Maths"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                className="border-slate-200"
                autoFocus
                data-testid="folder-name-input"
              />
              <Button onClick={handleCreateFolder} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="create-folder-btn">
                Create Folder
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-slate-900">Rename Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <Input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                className="border-slate-200"
                autoFocus
                data-testid="rename-input"
              />
              <Button onClick={handleRename} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="rename-confirm-btn">
                Rename
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
